import React, { useEffect, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope'
import { faLock } from '@fortawesome/pro-regular-svg-icons/faLock'
import { Control, Controller, FieldErrorsImpl, useForm, UseFormRegister } from 'react-hook-form'

import Alert from '../../../../common/components/Alert'
import Button from '../../../../common/components/Button'
import TextField from '../../../../common/components/TextField'
import LoadingDots from '../../../../common/components/LoadingDots'
import { LoginFormContainer } from './styles'
import { dispatchCustomEvent } from '../../../../common/services/eventService'
import { CaptchaProps, ReCaptcha } from '../../../../common/captcha/ReCaptcha'
import CheckBox from '../../../../common/components/CheckBox'
import csrfToken from '../../../../common/utils/csrf'
import refreshPage from '@/common/utils/refreshPage'
import { sendBroadcastMessage } from '@/common/services/broadcastService'
import { updateAppData } from '@/common/hooks/useAppData'

export interface LoginFormProps {
  sessionExpired?: boolean
}

interface LoginFormFields {
  username: string
  password: string
  rememberMe: boolean
  'g-recaptcha-response'?: string
}

interface LoginFieldsProps {
  captcha?: CaptchaProps
  sessionExpired?: boolean
  loading?: boolean
  errors: FieldErrorsImpl<LoginFormFields>
  t: TFunction
  register: UseFormRegister<LoginFormFields>
  errorMessage?: string
  control: Control<any>
}

const captchaEnabled = window.App?.captcha?.enabled
const hasError = window.__GLOBAL_DATA__?.param?.error?.length > 0
const hasLoggedOut = window.__GLOBAL_DATA__?.param?.logout?.length > 0

const LoginFields: React.FC<LoginFieldsProps> = ({
  captcha,
  sessionExpired,
  loading,
  errors,
  register,
  t,
  errorMessage,
  control,
}) => (
  <>
    {sessionExpired && <h2>{t('common:sessionExpiredModal')}</h2>}
    <TextField
      icon={faEnvelope}
      label={t('email')}
      id="username"
      type="text"
      fullWidth
      autoComplete={'username'}
      data-is-sensitive="true"
      disabled={loading}
      error={!!errors.username}
      {...register('username', { required: true })}
    />
    <TextField
      icon={faLock}
      label={t('password')}
      id="password"
      type="password"
      fullWidth
      autoComplete={'current-password'}
      data-is-sensitive="true"
      disabled={loading}
      error={!!errors.password}
      {...register('password', { required: true })}
    />
    {!sessionExpired && (
      <a className="link" href="/reset">
        {t('forgotPassword')}
      </a>
    )}
    <Controller
      control={control}
      name={'rememberMe'}
      render={({ field: { onChange, value } }) => (
        <CheckBox
          onChange={onChange}
          checkedStatus={value}
          label={t('common:rememberMe')}
          description={t('common:rememberMe')}
          className={'check-box'}
        />
      )}
    />

    <Button
      size="md"
      fullWidth={!sessionExpired}
      color="blue"
      className="submit-button"
      type="submit"
      disabled={(captchaEnabled && !captcha?.isReady) || loading}
      form="login"
      id="login-button"
    >
      {loading ? <LoadingDots size={4} withText /> : t('login')}
    </Button>
    {!!errorMessage && <Alert color="red">{errorMessage}</Alert>}
  </>
)

const LoginForm: React.FC<LoginFormProps> = ({ sessionExpired }) => {
  const { t } = useTranslation(['login', 'errorsFrontend', 'errorsBackend'])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (hasError) {
      if (window.__GLOBAL_DATA__.param.type == 'captcha') {
        setErrorMessage(t('invalidCaptcha'))
      } else {
        setErrorMessage(t('invalidCredentials'))
      }
    }

    if (hasLoggedOut) {
      setErrorMessage(t('loggedOut'))
    }
  }, [])

  const queryClient = useQueryClient()
  const handleRefetchAll = () => queryClient.invalidateQueries()

  const handleGlobalVariablesUpdate = () => {
    fetch('/')
      .then((response) => response.text())
      .then((text) => {
        const match = text.match(/\/\/APP_START_DO_NOT_MODIFY_THIS_COMMENT_OR_YOU_MAY_HAVE_A_BAD_DAY\s*([\s\S]*?)\s*\/\/APP_END_DO_NOT_MODIFY_THIS_COMMENT_OR_YOU_MAY_HAVE_A_BAD_DAY/)
        if (!match || !match[1]) return

        const App = JSON.parse(match[1])
        window.App = App
        updateAppData(App)
      })
      .catch(() => {
        sendBroadcastMessage('redirect-to-root')
        refreshPage()
      })
  }

  const {
    register,
    handleSubmit: handleHookFormSubmit,
    formState: { errors },
    control,
  } = useForm<LoginFormFields>()

  const onSubmit = async ({ username, password, rememberMe, 'g-recaptcha-response': token }: LoginFormFields) => {
    setLoading(true)

    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    formData.append('remember-me', `${rememberMe}`)

    if (token) {
      formData.append('g-recaptcha-response', token)
    }
    try {
      const res = await fetch('/login', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-XSRF-TOKEN': csrfToken(),
        },
      })
      if (!res.ok) {
        const jsonRes = await res.json()
        if (jsonRes?.code) {
          setErrorMessage(t(`errorsBackend:${jsonRes?.code}`))
        } else if ([400, 401, 404].includes(res.status)) {
          setErrorMessage(t('login:invalidCredentials'))
        } else {
          setErrorMessage(t('errorsFrontend:unexpectedError'))
        }
        setLoading(false)
      } else {
        dispatchCustomEvent<boolean>('@@SESSION_EXPIRED', false)
        dispatchCustomEvent<boolean>('@@SHOW_LOGIN_MODAL', false)
        dispatchCustomEvent<boolean>('@@SHOW_ERROR', null)

        const bc = new BroadcastChannel('email_session')
        bc.postMessage(username)
        bc.close()

        if (document.location.pathname === '/login') {
          if (res.url && res.url.includes('logout')) {
            document.location = '/'
          } else {
            document.location = res.url || '/'
          }
        } else {
          if (username !== window?.App?.user?.email) window.location.href = '/'
          handleRefetchAll()
          handleGlobalVariablesUpdate()
        }
      }
      return
    } catch (e) {
      setErrorMessage(t('errorsFrontend:connectionError'))
    }
    setLoading(false)
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>, captcha?: CaptchaProps) {
    e.preventDefault()
    const token = captchaEnabled ? await captcha.execute() : null
    await handleHookFormSubmit((data) => onSubmit({ 'g-recaptcha-response': token, ...data }))(e)
  }

  return (
    <LoginFormContainer sessionExpired={sessionExpired}>
      {captchaEnabled ? (
        <ReCaptcha action="login">
          {(captcha) => (
            <form onSubmit={(e) => handleSubmit(e, captcha)} id="login">
              <LoginFields
                captcha={captcha}
                sessionExpired={sessionExpired}
                loading={loading}
                errors={errors}
                register={register}
                t={t}
                errorMessage={errorMessage}
                control={control}
              />
            </form>
          )}
        </ReCaptcha>
      ) : (
        <form onSubmit={handleHookFormSubmit(onSubmit)} id="login">
          <LoginFields
            sessionExpired={sessionExpired}
            loading={loading}
            errors={errors}
            register={register}
            t={t}
            errorMessage={errorMessage}
            control={control}
          />
        </form>
      )}
    </LoginFormContainer>
  )
}
export default LoginForm
